import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import { ThreeEvent, useThree } from '@react-three/fiber';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { useZScale } from './SceneCanvas';
import * as THREE from 'three';

const DraggableAreaOfInterest: React.FC = () => {
  const areaOfInterest = useSceneStore((state: SceneState) => state.sceneOptions.areaOfInterest);
  const setSceneOptions = useSceneStore((state: SceneState) => state.setSceneOptions);
  const setIsDragging = useSceneStore((state: SceneState) => state.setIsDragging);
  const setHeatmapData = useSceneStore((state: SceneState) => state.setHeatmapData);
  const useOrthographic = useSceneStore((state: SceneState) => state.useOrthographic);
  const zScale = useZScale();
  const { camera, raycaster, mouse } = useThree();
  
  const [isDraggingCorners, setIsDraggingCorners] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; worldX: number; worldY: number } | null>(null);
  const [initialArea, setInitialArea] = useState<typeof areaOfInterest | null>(null);
  const [draggedCorner, setDraggedCorner] = useState<string | null>(null);
  
  const groupRef = useRef<THREE.Group>(null);
  const isDraggingRef = useRef(false);

  // Create line object for the perimeter
  const lineObject = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const points = [
      new THREE.Vector3(areaOfInterest.min_x, areaOfInterest.min_y, 0.04 * zScale),
      new THREE.Vector3(areaOfInterest.max_x, areaOfInterest.min_y, 0.04 * zScale),
      new THREE.Vector3(areaOfInterest.max_x, areaOfInterest.max_y, 0.04 * zScale),
      new THREE.Vector3(areaOfInterest.min_x, areaOfInterest.max_y, 0.04 * zScale),
      new THREE.Vector3(areaOfInterest.min_x, areaOfInterest.min_y, 0.04 * zScale),
    ];
    geometry.setFromPoints(points);
    return new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: "#4f46e5", linewidth: 2 }));
  }, [areaOfInterest, zScale]);

  const handleCornerDown = (event: ThreeEvent<PointerEvent>, corner: string) => {
    event.stopPropagation();
    event.nativeEvent.stopPropagation();
    
    // Set dragging states immediately and synchronously
    isDraggingRef.current = true;
    setIsDragging(true);
    setIsDraggingCorners(true);
    setDraggedCorner(corner);
    
    // Disable pointer events on canvas to prevent OrbitControls interference
    const canvas = document.querySelector('canvas');
    if (canvas) {
      canvas.style.pointerEvents = 'none';
    }
    
    // Directly disable OrbitControls immediately
    const orbitControls = document.querySelector('canvas')?.parentElement?.querySelector('[data-orbit-controls]');
    if (orbitControls) {
      (orbitControls as any).enabled = false;
    }
    
    // Store both screen and world coordinates for global pointer move handler
    setDragStart({ 
      x: event.nativeEvent.clientX, 
      y: event.nativeEvent.clientY,
      worldX: event.point.x,
      worldY: event.point.y
    });
    setInitialArea({ ...areaOfInterest });
  };


  const handleCornerUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    setIsDraggingCorners(false);
    setDragStart(null);
    setInitialArea(null);
    setDraggedCorner(null);
    
    // Re-enable pointer events on canvas
    const canvas = document.querySelector('canvas');
    if (canvas) {
      canvas.style.pointerEvents = 'auto';
    }
    
    // Keep OrbitControls disabled until next pointerover/down to avoid mid-frame re-enabling
  }, [setIsDragging]);

  // Global event listeners to ensure drag state is maintained
  useEffect(() => {
    const handleGlobalPointerUp = () => {
      if (isDraggingRef.current) {
        handleCornerUp();
      }
    };

    const handleGlobalPointerMove = (event: PointerEvent) => {
      if (isDraggingRef.current && dragStart && initialArea && draggedCorner) {
        // Get the canvas element
        const canvas = document.querySelector('canvas');
        if (!canvas) return;
        
        const rect = canvas.getBoundingClientRect();
        
        // Convert screen coordinates to normalized device coordinates
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        
        let deltaX: number, deltaY: number;
        
        if (useOrthographic) {
          // For orthographic view, use screen coordinate deltas
          const screenDeltaX = event.clientX - dragStart.x;
          const screenDeltaY = event.clientY - dragStart.y;
          
          // Convert screen delta to world delta using camera bounds
          const orthoCamera = camera as THREE.OrthographicCamera;
          const worldWidth = orthoCamera.right - orthoCamera.left;
          const worldHeight = orthoCamera.top - orthoCamera.bottom;
          const canvas = document.querySelector('canvas');
          const rect = canvas?.getBoundingClientRect();
          
          if (rect) {
            deltaX = (screenDeltaX / rect.width) * worldWidth;
            deltaY = -(screenDeltaY / rect.height) * worldHeight; // Negative because screen Y is inverted
          } else {
            deltaX = 0;
            deltaY = 0;
          }
        } else {
          // For perspective view, use raycasting
          const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
          raycaster.setFromCamera(mouse, camera);
          
          const intersectionPoint = new THREE.Vector3();
          raycaster.ray.intersectPlane(plane, intersectionPoint);
          
          deltaX = intersectionPoint.x - dragStart.worldX;
          deltaY = intersectionPoint.y - dragStart.worldY;
        }
        
        let newArea = { ...initialArea };
        
        switch (draggedCorner) {
          case 'bottom-left':
            newArea.min_x = initialArea.min_x + deltaX;
            newArea.min_y = initialArea.min_y + deltaY;
            break;
          case 'bottom-right':
            newArea.max_x = initialArea.max_x + deltaX;
            newArea.min_y = initialArea.min_y + deltaY;
            break;
          case 'top-left':
            newArea.min_x = initialArea.min_x + deltaX;
            newArea.max_y = initialArea.max_y + deltaY;
            break;
          case 'top-right':
            newArea.max_x = initialArea.max_x + deltaX;
            newArea.max_y = initialArea.max_y + deltaY;
            break;
        }
        
        // Ensure minimum size
        if (newArea.max_x - newArea.min_x < 10) {
          if (draggedCorner.includes('left')) {
            newArea.min_x = newArea.max_x - 10;
          } else {
            newArea.max_x = newArea.min_x + 10;
          }
        }
        if (newArea.max_y - newArea.min_y < 10) {
          if (draggedCorner.includes('bottom')) {
            newArea.min_y = newArea.max_y - 10;
          } else {
            newArea.max_y = newArea.min_y + 10;
          }
        }
        
        setSceneOptions({ areaOfInterest: newArea });
        // Clear heatmap data when AOI is resized since it's no longer valid
        setHeatmapData(null);
      }
    };

    if (isDraggingRef.current) {
      document.addEventListener('pointerup', handleGlobalPointerUp);
      document.addEventListener('pointercancel', handleGlobalPointerUp);
      document.addEventListener('pointermove', handleGlobalPointerMove);
    }

    return () => {
      document.removeEventListener('pointerup', handleGlobalPointerUp);
      document.removeEventListener('pointercancel', handleGlobalPointerUp);
      document.removeEventListener('pointermove', handleGlobalPointerMove);
    };
  }, [isDraggingCorners, dragStart, initialArea, draggedCorner, setSceneOptions, setHeatmapData, camera, mouse, raycaster, useOrthographic, handleCornerUp]);


  return (
    <group ref={groupRef}>
      {/* Perimeter outline */}
      <primitive object={lineObject} />
      
      {/* Corner handles for resizing */}
      {[
        { x: areaOfInterest.min_x, y: areaOfInterest.min_y, name: 'bottom-left' },
        { x: areaOfInterest.max_x, y: areaOfInterest.min_y, name: 'bottom-right' },
        { x: areaOfInterest.min_x, y: areaOfInterest.max_y, name: 'top-left' },
        { x: areaOfInterest.max_x, y: areaOfInterest.max_y, name: 'top-right' },
      ].map((corner, index) => (
        <mesh
          key={index}
          position={[corner.x, corner.y, 0.15 * zScale]}
          scale={[0.5 * zScale, 0.5 * zScale, 0.5 * zScale]}
          onPointerOver={() => {
            // Pre-disable controls before pointerdown to avoid OrbitControls capturing
            // Don't set isDragging here as it causes point cloud re-renders on hover
          }}
          onPointerDown={(e) => handleCornerDown(e, corner.name)}
          onPointerUp={handleCornerUp}
          onPointerLeave={() => {
            // No need to re-enable controls here since we don't disable them on hover
          }}
        >
          <boxGeometry args={[4, 4, 2]} />
          <meshBasicMaterial color="#4f46e5" />
        </mesh>
      ))}
    </group>
  );
};

export default DraggableAreaOfInterest;
